/**
 * Instructor service for instructor panel operations
 */

import { apiClient } from './api';
import type { UserReference } from '../types';

// Instructor interfaces
export interface InstructorOverviewStats {
  active_runs: number;
  users_with_runs: number;
  recent_grades: number;
  overridden_grades: number;
  total_users: number;
  total_labs: number;
  average_system_grade?: number;
}

export interface UserActivitySummary {
  user_id: number;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  cohort?: string | null;
  assigned_instructor_id?: number | null;
  assigned_instructor?: UserReference | null;
  total_runs: number;
  active_runs: number;
  completed_runs: number;
  average_grade?: number;
  last_activity?: string;
}

export interface UserSummary {
  id: number;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  cohort?: string | null;
  assigned_instructor_id?: number | null;
  assigned_instructor?: UserReference | null;
  role: string;
  created_at: string;
}

export interface UserLabHistory {
  user: UserSummary;
  runs: RunWithDetails[];
  grades: GradeWithOverrides[];
  total_labs_attempted: number;
  total_labs_completed: number;
  average_score?: number;
}

export interface LabUsageStats {
  lab_id: string;
  lab_title: string;
  total_runs: number;
  unique_users: number;
  average_completion_time?: number;
  average_grade?: number;
  success_rate: number;
}

export interface RunGradeSummary {
  id: number;
  run_id: number;
  score: number;
  raw_score: number;
  total_points: number;
  percentage: number;
  created_at: string;
  overridden: boolean;
}

export interface RunWithDetails {
  id: number;
  user_id: number;
  lab_id: string;
  state: string;
  droplet_id?: string;
  ip_address?: string;
  ttl_expires_at: string;
  created_at: string;
  updated_at: string;
  grade_count: number;
  latest_grade?: RunGradeSummary | null;
  lab?: {
    slug: string;
    title: string;
    difficulty: string;
    estimated_minutes: number;
  };
  user?: {
    id: number;
    username: string;
    email: string;
    role: string;
  };
}

export interface GradeWithOverrides {
  id: number;
  run_id: number;
  score: number;
  total_points: number;
  report: Record<string, any>;
  created_at: string;
  overrides: GradeOverride[];
  run?: {
    id: number;
    user_id: number;
    lab_id: string;
    lab?: {
      slug: string;
      title: string;
    };
    user?: {
      id: number;
      username: string;
      email: string;
    };
  };
}

export interface GradeOverride {
  id: number;
  grade_id: number;
  instructor_id: number;
  new_score: number;
  comment: string;
  created_at: string;
  instructor?: {
    id: number;
    username: string;
    email: string;
  };
}

export interface GradeOverrideCreate {
  new_score: number;
  comment: string;
}

export interface RunFilters {
  state?: string;
  user_id?: number;
  lab_id?: string;
  limit?: number;
  offset?: number;
}

export interface GradeFilters {
  run_id?: number;
  user_id?: number;
  lab_id?: string;
  has_override?: boolean;
  limit?: number;
  offset?: number;
}

export interface UserActivityFilters {
  active_only?: boolean;
  include_failed?: boolean;
  assigned_instructor_id?: number;
  unassigned?: boolean;
  cohort?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface InstructorListFilters {
  assigned_instructor_id?: number;
  unassigned?: boolean;
  cohort?: string;
  search?: string;
}

export interface InstructorAssignmentUpdate {
  assigned_instructor_id: number | null;
}

export class InstructorService {
  /**
   * Get overview statistics for instructor dashboard
   */
  static async getOverviewStats(): Promise<InstructorOverviewStats> {
    const response = await apiClient.get('/instructor/stats/overview');
    return response;
  }

  static async getOverviewStatsFiltered(filters: UserActivityFilters = {}): Promise<InstructorOverviewStats> {
    const response = await apiClient.get('/instructor/stats/overview', filters);
    return response;
  }

  static async getLearners(filters: InstructorListFilters = {}): Promise<UserSummary[]> {
    const response = await apiClient.get('/instructor/learners', filters);
    return response;
  }

  static async getInstructors(): Promise<UserSummary[]> {
    const response = await apiClient.get('/instructor/instructors');
    return response;
  }

  /**
   * Get all runs with optional filtering
   */
  static async getAllRuns(filters: RunFilters = {}): Promise<RunWithDetails[]> {
    const response = await apiClient.get('/instructor/runs', filters);
    return response;
  }

  /**
   * Get all grades with optional filtering
   */
  static async getAllGrades(filters: GradeFilters = {}): Promise<GradeWithOverrides[]> {
    const response = await apiClient.get('/instructor/grades', filters);
    return response;
  }

  /**
   * Get user activity summary
   */
  static async getUserActivity(filters: UserActivityFilters = {}): Promise<UserActivitySummary[]> {
    const response = await apiClient.get('/instructor/users/activity', filters);
    return response;
  }

  static async assignLearnerInstructor(
    learnerId: number,
    data: InstructorAssignmentUpdate
  ): Promise<UserSummary> {
    const response = await apiClient.patch(
      `/instructor/learners/${learnerId}/assigned-instructor`,
      data
    );
    return response;
  }

  /**
   * Get detailed lab history for a specific user
   */
  static async getUserLabHistory(
    userId: number,
    includeGrades = true,
    includeFailed = false
  ): Promise<UserLabHistory> {
    const response = await apiClient.get(`/instructor/users/${userId}/labs`, {
      include_grades: includeGrades,
      include_failed: includeFailed,
    });
    return response;
  }

  /**
   * Get lab usage statistics
   */
  static async getLabUsageStats(timeframeDays = 30): Promise<LabUsageStats[]> {
    const response = await apiClient.get('/instructor/labs/usage', {
      timeframe_days: timeframeDays,
    });
    return response;
  }

  /**
   * Override a grade with a new score and comment
   */
  static async overrideGrade(gradeId: number, data: GradeOverrideCreate): Promise<GradeOverride> {
    const response = await apiClient.post(`/instructor/grades/${gradeId}/override`, data);
    return response;
  }

  /**
   * Get all overrides for a specific grade
   */
  static async getGradeOverrides(gradeId: number): Promise<GradeOverride[]> {
    const response = await apiClient.get(`/instructor/grades/${gradeId}/overrides`);
    return response;
  }
}
